import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, AlertTriangle, Users, TrendingDown, Loader2, ShieldAlert } from 'lucide-react';
import { auraApi } from '../../api';

/**
 * Panel zdrowia społeczności — widoczny tylko dla owner/admin/moderator.
 * Pokazuje: średni Aura Score, rozkład ryzyka (low/medium/high/critical),
 * listę at-risk members z churn probability, predykcję churn 30 dni.
 *
 * Backend: GET /api/aura/community/:id/health
 */
export default function CommunityHealthPanel({ communityId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!communityId) return;
    (async () => {
      try {
        const r = await auraApi.getCommunityHealth(communityId);
        setData(r.data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 403) setError('forbidden');
        else setError(e?.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [communityId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin mx-auto" />
      </div>
    );
  }

  // 403: user nie jest owner/admin/moderator — nie pokazuj nic (cicho)
  if (error === 'forbidden') return null;
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-rose-200 p-4 mt-6 text-sm text-rose-700">
        <AlertTriangle className="w-4 h-4 inline mr-1" /> Health panel: {error}
      </div>
    );
  }
  if (!data) return null;

  const avg = data.average_aura_score || 50;
  const total = data.total_members || 0;
  const pct = (n) => total ? Math.round((n / total) * 100) : 0;
  const churn30 = data.churn_prediction_30_days || 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-emerald-500" />
        Zdrowie społeczności
        <span className="text-xs font-normal text-slate-500">(widoczne tylko dla owner/admin/moderator)</span>
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> Członków</div>
          <div className="text-2xl font-bold text-slate-900">{total}</div>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <div className="text-xs text-emerald-700 mb-1">Średni Aura Score</div>
          <div className="text-2xl font-bold text-emerald-700">{avg.toFixed(1)}</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3">
          <div className="text-xs text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Wysokie ryzyko</div>
          <div className="text-2xl font-bold text-amber-700">{(data.high_risk_count || 0) + (data.critical_risk_count || 0)}</div>
        </div>
        <div className="bg-rose-50 rounded-lg p-3">
          <div className="text-xs text-rose-700 mb-1 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Churn ~30 dni</div>
          <div className="text-2xl font-bold text-rose-700">{churn30}</div>
        </div>
      </div>

      {total > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Rozkład ryzyka</div>
          <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
            <div className="bg-emerald-500" style={{ width: `${pct(data.low_risk_count || 0)}%` }} title={`Low: ${data.low_risk_count}`} />
            <div className="bg-amber-400" style={{ width: `${pct(data.medium_risk_count || 0)}%` }} title={`Medium: ${data.medium_risk_count}`} />
            <div className="bg-orange-500" style={{ width: `${pct(data.high_risk_count || 0)}%` }} title={`High: ${data.high_risk_count}`} />
            <div className="bg-rose-600" style={{ width: `${pct(data.critical_risk_count || 0)}%` }} title={`Critical: ${data.critical_risk_count}`} />
          </div>
          <div className="flex gap-3 text-[11px] text-slate-500 mt-1">
            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Low {data.low_risk_count || 0}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />Medium {data.medium_risk_count || 0}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-1" />High {data.high_risk_count || 0}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-rose-600 mr-1" />Critical {data.critical_risk_count || 0}</span>
          </div>
        </div>
      )}

      {data.at_risk_members?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Members at risk (top 10 wg churn prob.)
          </div>
          <div className="space-y-1.5">
            {data.at_risk_members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm">
                <span className="text-slate-700 font-mono text-xs truncate flex-1">{m.user_id}</span>
                <span className="text-xs text-slate-500 mx-2">score: {m.score}</span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  m.risk_level === 'critical' ? 'bg-rose-100 text-rose-700' :
                  m.risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {m.risk_level}
                </span>
                <span className="text-xs text-rose-600 ml-2 w-12 text-right">{Math.round((m.churn_probability || 0) * 100)}%</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Wskazówka: skontaktuj się z tymi osobami — DM, zaproszenie do dyskusji albo personalna wiadomość owner'a.
          </p>
        </div>
      )}

      {(!data.at_risk_members || data.at_risk_members.length === 0) && total > 0 && (
        <div className="text-sm text-emerald-700 bg-emerald-50 rounded p-3">
          ✓ Brak members at risk — społeczność jest zdrowa.
        </div>
      )}
    </div>
  );
}
